/**
 * steps.js
 * Equal card heights for the `steps` section — but only across COLLAPSED cards.
 *
 * Why this can't be CSS: a grid row is always sized to the tallest item in it,
 * and `align-items` only decides whether items fill that height. So `stretch`
 * makes every card grow when one is expanded, and `start` lets collapsed cards
 * fall to their own uneven heights. "Match the tallest card as if all were
 * collapsed" is a value CSS cannot compute, so it is measured here and written
 * to `--steps-card-min-height`; the stylesheet pairs it with `align-items:
 * start` so an expanded card grows past the floor on its own.
 *
 * The measurement never reads a card's open/closed state — a collapsed card is
 * exactly its padding plus each of its direct children, with the <details>
 * counted as its `.steps__summary` alone, and none of those change when the
 * disclosure opens. That is why there is no `toggle` listener: opening a card
 * cannot invalidate the floor.
 *
 * Children are walked generically rather than looked up by name so the optional
 * card-level icon slots (`steps__icon--slot-top` / `--slot-bottom`) are counted
 * without this file needing to know about them.
 *
 * Mobile (<750px) stacks rows and overlays the summary on the info, so the sum
 * above is meaningless there — the class is removed below the breakpoint and
 * the stylesheet scopes the `min-height` to >=750px as a second guard.
 *
 * Scoped per grid; re-runs on shopify:section:load in the theme editor. With JS
 * off, the class is never added and cards fall back to the CSS `stretch`
 * default — equal heights, expanded card included.
 */
(function () {
  'use strict';

  var BREAKPOINT = '(min-width: 750px)';
  var MEASURED = 'steps__grid--measured';

  function collapsedHeight(card) {
    var styles = window.getComputedStyle(card);
    var height =
      parseFloat(styles.paddingTop) +
      parseFloat(styles.paddingBottom) +
      parseFloat(styles.borderTopWidth) +
      parseFloat(styles.borderBottomWidth);

    var gap = parseFloat(styles.rowGap) || 0;
    var counted = 0;

    Array.prototype.slice.call(card.children).forEach(function (child) {
      var measured;

      if (child.classList.contains('steps__disclosure')) {
        // An OPEN <details> is taller than its collapsed self, so its own box
        // is never measured — only the summary row, which is what a collapsed
        // card would show. A step with no description renders no <details> at
        // all and contributes nothing here, not even a gap.
        var summary = child.querySelector('.steps__summary');
        if (!summary) return;
        measured = summary.getBoundingClientRect().height;
      } else {
        measured = child.getBoundingClientRect().height;
      }

      height += measured;
      counted += 1;
    });

    if (counted > 1) height += gap * (counted - 1);

    return height;
  }

  function measure(grid) {
    if (!window.matchMedia(BREAKPOINT).matches) {
      grid.classList.remove(MEASURED);
      grid.style.removeProperty('--steps-card-min-height');
      return;
    }

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.steps__card'));
    if (!cards.length) return;

    var tallest = cards.reduce(function (max, card) {
      return Math.max(max, collapsedHeight(card));
    }, 0);

    grid.style.setProperty('--steps-card-min-height', Math.ceil(tallest) + 'px');
    grid.classList.add(MEASURED);
  }

  function initGrid(grid) {
    if (!grid || grid.dataset.stepsHeightReady === 'true') return;
    grid.dataset.stepsHeightReady = 'true';

    var ticking = false;
    function requestTick() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        measure(grid);
      });
    }

    window.addEventListener('resize', requestTick);
    measure(grid);

    // Titles re-wrap once the webfont swaps in, which changes the floor.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        measure(grid);
      });
    }
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(root.querySelectorAll('.steps__grid')).forEach(initGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var grid = e.target && e.target.querySelector ? e.target.querySelector('.steps__grid') : null;
    if (grid) {
      grid.dataset.stepsHeightReady = '';
      initGrid(grid);
    }
  });
})();
